/** @file uiActions: the pure UI slice of EditorActions, shared by the server and memory stores. */
import type { EditorActions, EditorState, Toast } from './editorTypes';
import { sameTile } from './sameTile';

type Set = (partial: Partial<EditorState> | ((state: EditorState) => Partial<EditorState>)) => void;

/**
 * The actions of `EditorActions` that only touch local UI state (selection, tool, playhead, camera, menu, shell, toasts),
 * plus `redo` as a no-op for a store that has no revision log to redo from (a store that has one spreads its own after).
 */
export type UiActions = Pick<EditorActions,
  'navigate' | 'select' | 'setTool' | 'setSpace' | 'setPivot' | 'setSnap' | 'setPlayhead' | 'setCamera'
  | 'setPoseOverride' | 'openMenu' | 'closeMenu' | 'setShell' | 'openTile' | 'closeTile' | 'notify' | 'dismissToast' | 'redo'>;

/** Builds the UI actions over a zustand `set`; toast ids count up from 1. */
export function uiActions(set: Set): UiActions {
  let nextToast = 1;
  return {
    navigate: (route) => { set((s) => ({ route, menu: null, shell: { ...s.shell, menuBar: null, dialog: null } })); },
    select: (selection) => { set((s) => ({ selection: { ...s.selection, ...selection } })); },
    setTool: (tool) => { set({ tool }); },
    setSpace: (space) => { set({ space }); },
    setPivot: (pivot) => { set({ pivot }); },
    setSnap: (snapDeg) => { set({ snapDeg }); },
    setPlayhead: (playhead) => { set((s) => ({ playhead: { ...s.playhead, ...playhead } })); },
    setCamera: (camera) => { set((s) => ({ camera: { ...s.camera, ...camera } })); },
    setPoseOverride: (poseOverride) => { set({ poseOverride }); },
    openMenu: (x, y, items) => { set({ menu: { x, y, items } }); },
    closeMenu: () => { set({ menu: null }); },
    setShell: (patch) => { set((s) => ({ shell: { ...s.shell, ...patch } })); },
    openTile: (tile) => {
      set((s) => {
        const puppetId = s.puppet.id ?? ('puppetId' in s.route ? s.route.puppetId : null);
        if (puppetId === null) return {};
        const tiles = s.shell.tiles.some((t) => sameTile(t, tile)) ? s.shell.tiles : [...s.shell.tiles, tile];
        return { route: { screen: 'tile', puppetId, tile }, menu: null, shell: { ...s.shell, menuBar: null, dialog: null, tiles } };
      });
    },
    closeTile: (tile) => {
      set((s) => {
        const tiles = s.shell.tiles.filter((t) => !sameTile(t, tile));
        const shell = { ...s.shell, tiles };
        if (s.route.screen === 'tile' && sameTile(s.route.tile, tile)) return { shell, route: { screen: 'pose', puppetId: s.route.puppetId } };
        return { shell };
      });
    },
    notify: (kind: Toast['kind'], text: string) => {
      const id = nextToast++;
      set((s) => ({ toasts: [...s.toasts, { id, kind, text }] }));
    },
    dismissToast: (id) => { set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })); },
    redo: () => Promise.resolve(),
  };
}
